/** Game constants: bot AI and bot commands. Part of src/constants.ts (re-exported there; import from '../constants'). */
import type { BotPersonality } from '../types';

// =============================================================================
// BOT AI
// =============================================================================

export const BOT_PRESETS: Record<string, BotPersonality> = {
  aggressive: { aggression: 0.8, mobility: 0.4, riskTolerance: 0.7 },
  defensive:  { aggression: 0.3, mobility: 0.2, riskTolerance: 0.2 },
  chaotic:    { aggression: 0.5, mobility: 0.8, riskTolerance: 0.9 },
  balanced:   { aggression: 0.5, mobility: 0.3, riskTolerance: 0.4 },
};

export const BOT_SKILL_LEVELS: Record<string, number> = {
  easy: 0.2,
  medium: 0.5,
  hard: 0.8,
  expert: 1.0,
};

// =============================================================================
// BOT COMMANDS
// =============================================================================

export const COMMAND_MOVEMENT_COST = 0.5;  // movement points per command
export const COMMAND_MAX_PER_TURN = 3;      // max commands per turn
export const COMMAND_EXPIRY_TURNS = 3;      // auto-expire after N turns

/**
 * §15.2: aim spread for a shot at a team-picture track (u, on the surface), by what made it: a unit seen or a drone's
 * identified sighting is tight; a teammate's report wider; a radar dot (position, no identity) or a shot's source
 * wide. Plus PICTURE_SPREAD_PER_TURN for each turn of age (about half a turn's walk: the likely drift, not the most).
 * Tracks under PICTURE_MIN_CONFIDENCE are not fired on.
 */
export const PICTURE_SPREAD: Record<'seen' | 'probe' | 'incomingFire' | 'heard' | 'evidence' | 'told' | 'sensor' | 'mark', number> = {
  seen: 0.3, probe: 0.4, told: 0.8, sensor: 2.5, incomingFire: 2.0, heard: 2.5, evidence: 3.0,
  mark: 0.3,   // X2: a commander's mark is the tightest fix there is
};
export const PICTURE_SPREAD_PER_TURN = 2;
export const PICTURE_MIN_CONFIDENCE = 0.2;

/**
 * The gun brain's work budget (CLOCK_RFC §7; ARCHITECTURE §5.5 lane B's answer): a gun crew's fire search runs this one
 * tier, whatever the bot's skill, so its worst case fits in about half of L (60 ticks = 0.5 s) on the reference
 * machine (decision 35). A count of work, never a timeout: the decision is the same on every machine, only its wall
 * time differs. Measured from a seat with every enemy known (scripts/probeGunSearch.ts, 12 maps × 3 searches, a loaded
 * laptop): this shape 152 ms mean / 246 ms worst on the WASM kernel (the browser's path), 153 / 262 ms on JS; the
 * skill tiers it replaces reached 301–473 ms worst (the top tier).
 */
export const GUN_SEARCH_TIER = { minSkill: 0, coarseAzSteps: 12, coarseElevSteps: 5, coarsePowSteps: 4, focusedTopN: 3, focusedGridSize: 5, refinePasses: 2, randomSeeds: 4 };
